import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  completePublicCapture,
  finalizePublicCapture,
  getPublicCaptureSession,
  mimeToCaptureFileType,
  presignPublicCapture,
  putCaptureBlob,
  PublicCaptureSession,
  CaptureKind,
} from "../../../services/productCaptureApi";
import {
  clearPendingCapture,
  loadPendingSet,
  PendingCapture,
  savePendingSet,
  SLOT_COUNT,
} from "./capturePendingStore";
import {
  geoErrorKind,
  geoErrorMessage,
  geoPermissionQueryUnreliable,
  mediaErrorKind,
  mediaErrorMessage,
  mergeAudioIntoStream,
  nextCaptureGestureNeed,
  permissionActionLabel,
  permissionBlockedHelp,
  permissionSettingsHelp,
  requestCameraStream,
  requestMicrophoneStream,
  watchBrowserPermission,
} from "./capturePermissions";
import "./capture-public.scss";
import { formatCaptureGeo, GeoFix, readClickGeo, requestGeoPosition } from "./captureGeo";
import { frameClass, frameFromSize, MediaFrame } from "../mediaFrame";

type Cell = {
  take: PendingCapture | null;
  previewUrl: string;
  sent: boolean;
};

const emptyCells = (): Cell[] =>
  Array.from({ length: SLOT_COUNT }, () => ({ take: null, previewUrl: "", sent: false }));

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isClosedStatus = (status?: string, submitted?: boolean) =>
  Boolean(submitted) || status === "SUBMITTED" || status === "USED_UP";

const pickRecorderMime = (): string => {
  const types = ["video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
  if (typeof MediaRecorder === "undefined") {
    return "";
  }
  return types.find((t) => MediaRecorder.isTypeSupported(t)) || "";
};

const compressPhotoFromVideo = (video: HTMLVideoElement): Promise<Blob> => {
  const max = 1280;
  const vw = video.videoWidth || 1280;
  const vh = video.videoHeight || 720;
  const scale = Math.min(1, max / Math.max(vw, vh));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(2, Math.round(vw * scale));
  canvas.height = Math.max(2, Math.round(vh * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return Promise.reject(new Error("Could not capture that photo"));
  }
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not compress that photo"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      0.8
    );
  });
};

const parseCaptureToken = (pathname: string): string => {
  const parts = String(pathname || "").split("/").filter(Boolean);
  const idx = parts.indexOf("capture");
  if (idx < 0 || !parts[idx + 1]) {
    return "";
  }
  try {
    return decodeURIComponent(parts[idx + 1]);
  } catch {
    return parts[idx + 1];
  }
};

const touchDistance = (touches: React.TouchList) => {
  if (touches.length < 2) return 0;
  return Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
};

type Asking = "camera" | "mic" | "geo" | "";

type PermissionRetryPanelProps = {
  allowsVideo: boolean;
  cameraReady: boolean;
  micReady: boolean;
  geoReady: boolean;
  camBlocked: boolean;
  micBlocked: boolean;
  geoBlocked: boolean;
  asking: Asking;
  permMessage: string;
  tone?: "page" | "sheet";
  onCamera: () => void;
  onMic: () => void;
  onGeo: () => void;
  onRetryAll: () => void;
};

const PermissionRetryPanel = ({
  allowsVideo,
  cameraReady,
  micReady,
  geoReady,
  camBlocked,
  micBlocked,
  geoBlocked,
  asking,
  permMessage,
  tone = "page",
  onCamera,
  onMic,
  onGeo,
  onRetryAll,
}: PermissionRetryPanelProps) => {
  const blocked = camBlocked || geoBlocked || (allowsVideo && micBlocked);
  const missing = [
    !geoReady ? "location" : "",
    geoReady && !cameraReady ? "camera" : "",
    geoReady && allowsVideo && !micReady ? "microphone" : "",
  ].filter(Boolean);
  if (!missing.length) return null;
  const title = !geoReady
    ? geoBlocked
      ? "Location was blocked. Follow the steps below, then tap Try location again."
      : "Allow location first. This phone can show only one permission prompt at a time."
    : blocked
    ? "Access was denied. Change site settings if no prompt appears, then try again."
    : missing.length === 1
    ? `Allow ${missing[0]} to click or record. There is no gallery upload on this page.`
    : `Allow ${missing.slice(0, -1).join(", ")} and ${
        missing[missing.length - 1]
      } to click or record. There is no gallery upload on this page.`;
  return (
    <div className={`capture-public__perms${tone === "sheet" ? " capture-public__perms--sheet" : ""}`} role="status">
      <p className="capture-public__perms-title">{title}</p>
      <div className="capture-public__actions capture-public__actions--perms">
        {!geoReady ? (
          <button className="capture-public__btn" type="button" disabled={asking === "geo"} onClick={onGeo}>
            {permissionActionLabel(asking === "geo", geoBlocked, "Allow location", "Try location again")}
          </button>
        ) : null}
        {geoReady && !cameraReady ? (
          <button className="capture-public__btn" type="button" disabled={asking === "camera"} onClick={onCamera}>
            {permissionActionLabel(asking === "camera", camBlocked, "Allow camera", "Try camera again")}
          </button>
        ) : null}
        {geoReady && allowsVideo && !micReady ? (
          <button className="capture-public__btn" type="button" disabled={asking === "mic"} onClick={onMic}>
            {permissionActionLabel(asking === "mic", micBlocked, "Allow microphone", "Try microphone again")}
          </button>
        ) : null}
        {blocked ? (
          <button
            className="capture-public__btn capture-public__btn--ghost"
            type="button"
            disabled={Boolean(asking)}
            onClick={onRetryAll}
          >
            {asking ? "Asking…" : "Try all again"}
          </button>
        ) : null}
      </div>
      {permMessage ? <p className="capture-public__error">{permMessage}</p> : null}
      <p className="capture-public__perms-help">
        {blocked
          ? permissionBlockedHelp(navigator.userAgent, "safari" in window)
          : permissionSettingsHelp(navigator.userAgent, "safari" in window)}
      </p>
    </div>
  );
};

const CapturePublicPage = () => {
  const params: any = useParams();
  const location = useLocation();
  const token = String(params?.token || parseCaptureToken(location.pathname)).trim();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const geoRef = useRef<GeoFix | null>(null);
  const timerRef = useRef<number | null>(null);
  const cellsRef = useRef<Cell[]>(emptyCells());
  const pinchStartRef = useRef(0);
  const pinchScaleRef = useRef(1);

  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<PublicCaptureSession | null>(null);
  const [error, setError] = useState("");
  const [geoReady, setGeoReady] = useState(false);
  const [geoLabel, setGeoLabel] = useState("");
  const [cameraReady, setCameraReady] = useState(false);
  const [micReady, setMicReady] = useState(false);
  const [geoBlocked, setGeoBlocked] = useState(false);
  const [camBlocked, setCamBlocked] = useState(false);
  const [micBlocked, setMicBlocked] = useState(false);
  const [asking, setAsking] = useState<"camera" | "mic" | "geo" | "">("");
  const [permMessage, setPermMessage] = useState("");
  const [recording, setRecording] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(45);
  const [cells, setCells] = useState<Cell[]>(emptyCells());
  const [cameraSlot, setCameraSlot] = useState<number | null>(null);
  const [reviewSlot, setReviewSlot] = useState<number | null>(null);
  const [reviewScale, setReviewScale] = useState(1);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mediaFrame, setMediaFrame] = useState<MediaFrame>("unknown");
  const camBlockedRef = useRef(false);
  const micBlockedRef = useRef(false);
  const geoBlockedRef = useRef(false);
  const cameraReadyRef = useRef(false);
  const micReadyRef = useRef(false);
  const geoReadyRef = useRef(false);
  const askingRef = useRef<"camera" | "mic" | "geo" | "">("");

  useEffect(() => {
    cellsRef.current = cells;
    camBlockedRef.current = camBlocked;
    micBlockedRef.current = micBlocked;
    geoBlockedRef.current = geoBlocked;
    cameraReadyRef.current = cameraReady;
    micReadyRef.current = micReady;
    geoReadyRef.current = geoReady;
    askingRef.current = asking;
  });

  const maxSeconds = session?.maxVideoSeconds || 45;
  const allowsPhoto = (session?.allowedKinds || ["PHOTO", "VIDEO"]).includes("PHOTO");
  const allowsVideo = (session?.allowedKinds || ["PHOTO", "VIDEO"]).includes("VIDEO");
  const open = session?.status === "OPEN" && !sessionClosed;
  const maxUploads = Math.min(SLOT_COUNT, Math.max(1, session?.maxUploads || SLOT_COUNT));
  const pendingCount = cells.filter((cell) => cell.take && !cell.sent).length;
  const sentCount = cells.filter((cell) => cell.sent).length;
  const emptyCount = Math.max(0, maxUploads - sentCount - pendingCount);
  const cameraCell = cameraSlot != null ? cells[cameraSlot] : null;
  const reviewCell = reviewSlot != null ? cells[reviewSlot] : null;
  const cameraOpen = open && cameraSlot != null && cameraCell && !cameraCell.sent && !cameraCell.take;
  const showSaved = !loading && (sessionClosed || isClosedStatus(session?.status, session?.submitted));

  useEffect(() => {
    setMediaFrame("unknown");
    setReviewScale(1);
    pinchScaleRef.current = 1;
  }, [reviewSlot, reviewCell?.previewUrl]);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
    setMicReady(false);
  };

  const attachStreamToVideo = async (stream: MediaStream) => {
    streamRef.current = stream;
    const video = videoRef.current;
    if (video) {
      video.srcObject = stream;
      await video.play().catch(() => undefined);
    }
  };

  const startCamera = async (fromUser = false) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamBlocked(true);
      setPermMessage("This phone cannot open the camera in the browser.");
      return;
    }
    setAsking("camera");
    try {
      const keepAudio = streamRef.current?.getAudioTracks().filter((track) => track.readyState === "live") || [];
      streamRef.current?.getVideoTracks().forEach((track) => track.stop());
      const stream = await requestCameraStream();
      keepAudio.forEach((track) => {
        if (!stream.getAudioTracks().some((existing) => existing.id === track.id)) {
          stream.addTrack(track);
        }
      });
      await attachStreamToVideo(stream);
      setCameraReady(true);
      setCamBlocked(false);
      setMicReady(stream.getAudioTracks().some((track) => track.readyState === "live"));
      setPermMessage("");
      setError("");
      if (allowsVideo && !stream.getAudioTracks().length) {
        try {
          const audio = await requestMicrophoneStream();
          mergeAudioIntoStream(stream, audio);
          setMicReady(true);
          setMicBlocked(false);
        } catch (micErr) {
          const micKind = mediaErrorKind(micErr);
          setMicReady(false);
          setMicBlocked(micKind === "denied");
          if (micKind === "denied") {
            setPermMessage(mediaErrorMessage(micKind, "microphone"));
          }
        }
      }
    } catch (err) {
      const kind = mediaErrorKind(err);
      setCameraReady(false);
      setCamBlocked(kind === "denied");
      setPermMessage(mediaErrorMessage(kind, "camera"));
    } finally {
      setAsking("");
    }
  };

  const requestMicrophone = async () => {
    setAsking("mic");
    try {
      const audio = await requestMicrophoneStream();
      const videoStream = streamRef.current;
      if (videoStream) {
        mergeAudioIntoStream(videoStream, audio);
      } else {
        streamRef.current = audio;
      }
      setMicReady(true);
      setMicBlocked(false);
      setPermMessage("");
    } catch (err) {
      const kind = mediaErrorKind(err);
      setMicReady(false);
      setMicBlocked(kind === "denied");
      setPermMessage(mediaErrorMessage(kind, "microphone"));
    } finally {
      setAsking("");
    }
  };

  const applyGeoFix = (pos: GeolocationPosition) => {
    const fix: GeoFix = {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracyM: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : 9999,
      at: Date.now(),
    };
    geoRef.current = fix;
    setGeoLabel(formatCaptureGeo(fix));
    setGeoReady(true);
    setGeoBlocked(false);
    setPermMessage("");
  };

  const requestLocation = (fromUser = true) => {
    if (!navigator.geolocation) {
      setGeoBlocked(true);
      setPermMessage("This phone cannot share location, so a live capture cannot be saved.");
      return;
    }
    setAsking("geo");
    askingRef.current = "geo";
    requestGeoPosition()
      .then((pos) => {
        applyGeoFix(pos);
      })
      .catch((err) => {
        const kind = geoErrorKind(err);
        if (!geoRef.current) setGeoReady(false);
        // iOS/Safari often reports denied before the site prompt. Only lock after a tap.
        if (fromUser || kind !== "denied") {
          setGeoBlocked(kind === "denied");
        }
        setPermMessage(geoErrorMessage(kind));
      })
      .finally(() => {
        askingRef.current = "";
        setAsking("");
      });
  };

  const retryNextPermission = () => {
    const need = nextCaptureGestureNeed({
      geoReady: geoReadyRef.current,
      cameraReady: cameraReadyRef.current,
      micReady: micReadyRef.current,
      allowsVideo,
    });
    if (need === "location") {
      requestLocation(true);
      return;
    }
    if (need === "camera") {
      void startCamera(true);
      return;
    }
    if (need === "microphone") {
      void requestMicrophone();
    }
  };

  const persistCells = async (next: Cell[]) => {
    await savePendingSet({
      token,
      items: next
        .map((cell, slotIndex) =>
          cell.take && !cell.sent ? { ...cell.take, slotIndex, note: note || cell.take.note } : null
        )
        .filter((item): item is PendingCapture => Boolean(item)),
    });
  };

  const lockSession = (result?: Partial<PublicCaptureSession>) => {
    setSessionClosed(true);
    setCameraSlot(null);
    setReviewSlot(null);
    setConfirmSend(false);
    stopStream();
    setSession((prev) =>
      prev
        ? {
            ...prev,
            status: result?.status || "SUBMITTED",
            submitted: true,
            remainingUploads: 0,
            usedCount: result?.usedCount ?? prev.usedCount,
            processTitle: result?.processTitle || prev.processTitle,
          }
        : prev
    );
  };

  useEffect(() => {
    if (!token) {
      setError("This capture link could not be read.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getPublicCaptureSession(token)
      .then(async (res) => {
        if (cancelled) return;
        if (res?.message === "FEATURE_DISABLED") {
          setError("Field capture is not available yet.");
          setSession(null);
          return;
        }
        const data = res?.data;
        if (!res?.success || !data) {
          setError(res?.message || "This capture link was not found.");
          setSession(data || null);
          return;
        }
        setSession(data);
        if (isClosedStatus(data.status, data.submitted)) {
          setSessionClosed(true);
          return;
        }
        if (data.status !== "OPEN") {
          setError(data.message || "This capture link cannot be used.");
        }
        const used = Math.min(SLOT_COUNT, Math.max(0, data.usedCount || 0));
        const next = emptyCells();
        for (let i = 0; i < used; i += 1) {
          next[i] = { take: null, previewUrl: "", sent: true };
        }
        const saved = await loadPendingSet(token);
        saved.items.forEach((item) => {
          const idx = Math.min(SLOT_COUNT - 1, Math.max(0, item.slotIndex ?? 0));
          if (next[idx].sent) return;
          next[idx] = {
            take: item,
            previewUrl: URL.createObjectURL(item.blob),
            sent: false,
          };
        });
        setCells(next);
        if (saved.items[0]?.note) {
          setNote(saved.items[0].note);
        }
      })
      .catch(() => {
        if (!cancelled) setError("Could not open this capture link right now.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setError("This phone cannot share location, so a live capture cannot be saved.");
      return;
    }
    if (!geoReady) {
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        applyGeoFix(pos);
      },
      () => undefined,
      { enableHighAccuracy: false, maximumAge: 30000, timeout: 30000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoReady]);

  useEffect(() => {
    if (!cameraOpen || sending) {
      return;
    }
    const stream = streamRef.current;
    const video = videoRef.current;
    if (stream && video) {
      video.srcObject = stream;
      void video.play().catch(() => undefined);
    }
  }, [cameraOpen, sending, cameraReady]);

  useEffect(() => {
    if (cameraOpen) return;
    stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOpen]);

  useEffect(() => {
    if (!open) return undefined;
    const retryBlocked = () => {
      if (geoBlockedRef.current && !geoReadyRef.current) {
        requestLocation(false);
        return;
      }
      if (camBlockedRef.current && !cameraReadyRef.current) {
        startCamera(true);
        return;
      }
      if (allowsVideo && micBlockedRef.current && !micReadyRef.current) {
        requestMicrophone();
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        retryBlocked();
      }
    };
    const stopCameraWatch = watchBrowserPermission("camera", (state) => {
      if (state === "denied") setCamBlocked(true);
      if (state === "granted" && camBlockedRef.current) startCamera(true);
    });
    const stopMicWatch = allowsVideo
      ? watchBrowserPermission("microphone", (state) => {
          if (state === "denied") setMicBlocked(true);
          if (state === "granted" && micBlockedRef.current) requestMicrophone();
        })
      : () => undefined;
    const stopGeoWatch = watchBrowserPermission("geolocation", (state) => {
      if (state === "granted") {
        if (!geoReadyRef.current) requestLocation(false);
        return;
      }
      // Safari/iOS Permissions API reports denied for Ask/Allow-global before this site is prompted.
      if (state === "denied" && !geoPermissionQueryUnreliable(navigator.userAgent, "safari" in window) && !geoReadyRef.current) {
        setGeoBlocked(true);
      }
    });
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", retryBlocked);
    return () => {
      stopCameraWatch();
      stopMicWatch();
      stopGeoWatch();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", retryBlocked);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allowsVideo]);

  useEffect(() => {
    return () => {
      cellsRef.current.forEach((cell) => {
        if (cell.previewUrl) URL.revokeObjectURL(cell.previewUrl);
      });
      if (timerRef.current) window.clearInterval(timerRef.current);
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showSaved) return;
    const closeTimer = window.setTimeout(() => {
      try {
        window.close();
      } catch {
        // Browser may block close when this tab was not opened by script.
      }
    }, 1800);
    return () => window.clearTimeout(closeTimer);
  }, [showSaved]);

  const tagClickGeo = async (): Promise<GeoFix> => {
    const geo = await readClickGeo(geoRef.current);
    geoRef.current = geo;
    setGeoLabel(formatCaptureGeo(geo));
    setGeoReady(true);
    return geo;
  };

  const storeTake = async (take: PendingCapture) => {
    const next = cells.map((cell, i) => {
      if (i !== take.slotIndex) return cell;
      if (cell.previewUrl) URL.revokeObjectURL(cell.previewUrl);
      return {
        take,
        previewUrl: URL.createObjectURL(take.blob),
        sent: false,
      };
    });
    setCells(next);
    await persistCells(next);
    setCameraSlot(null);
    stopStream();
  };

  const onClickPhoto = async () => {
    const video = videoRef.current;
    const slotIndex = cameraSlot;
    if (slotIndex == null) return;
    if (!video || video.videoWidth < 2) {
      setError("Camera is not ready yet.");
      return;
    }
    try {
      const geo = await tagClickGeo();
      const blob = await compressPhotoFromVideo(video);
      const maxBytes = session?.maxPhotoBytes || 8 * 1024 * 1024;
      if (blob.size > maxBytes) {
        setError("That photo is too large. Try again.");
        return;
      }
      await storeTake({
        token,
        slotIndex,
        kind: "PHOTO",
        mime: "image/jpeg",
        blob,
        lat: geo.lat,
        lng: geo.lng,
        accuracyM: geo.accuracyM,
        capturedAt: Date.now(),
        note,
      });
      setError("");
    } catch (e: any) {
      setError(e?.message || "Could not take that photo.");
    }
  };

  const stopRecording = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    setRecording(false);
  };

  const onRecord = async () => {
    let geo: GeoFix;
    try {
      geo = await tagClickGeo();
    } catch (e: any) {
      setError(e?.message || "Wait for location, then record.");
      return;
    }
    const stream = streamRef.current;
    if (!stream) {
      setError("Camera is not ready yet.");
      return;
    }
    const mime = pickRecorderMime();
    if (!mime && typeof MediaRecorder === "undefined") {
      setError("This browser cannot record video in the page. Use an updated Chrome or Safari.");
      return;
    }
    chunksRef.current = [];
    const capturedAt = Date.now();
    const slotIndex = cameraSlot;
    if (slotIndex == null) return;
    let recorder: MediaRecorder;
    try {
      recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 1_200_000 })
        : new MediaRecorder(stream, { videoBitsPerSecond: 1_200_000 });
    } catch {
      setError("Could not start live recording on this phone.");
      return;
    }
    recorderRef.current = recorder;
    recorder.ondataavailable = (evt) => {
      if (evt.data && evt.data.size) {
        chunksRef.current.push(evt.data);
      }
    };
    recorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mime || "video/webm" });
      const maxBytes = session?.maxVideoBytes || 15 * 1024 * 1024;
      if (blob.size > maxBytes) {
        setError("That video is too large. Record a shorter clip.");
        startCamera(true);
        return;
      }
      let stopGeo = geo;
      try {
        stopGeo = await tagClickGeo();
      } catch {
        stopGeo = geo;
      }
      await storeTake({
        token,
        slotIndex,
        kind: "VIDEO",
        mime: blob.type || "video/webm",
        blob,
        lat: stopGeo.lat,
        lng: stopGeo.lng,
        accuracyM: stopGeo.accuracyM,
        capturedAt,
        note,
      });
    };
    recorder.start(250);
    setRecording(true);
    setSecondsLeft(maxSeconds);
    let remaining = maxSeconds;
    timerRef.current = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        stopRecording();
        setSecondsLeft(0);
        return;
      }
      setSecondsLeft(remaining);
    }, 1000);
  };

  const sendOne = async (take: PendingCapture, finalize: boolean, onProgress: (n: number) => void) => {
    const kind: CaptureKind = take.kind;
    const fileType = mimeToCaptureFileType(take.mime, kind);
    let lastError = "Could not send.";
    for (let attempt = 0; attempt < 4; attempt += 1) {
      try {
        const presign = await presignPublicCapture(token, { fileType, kind });
        if (!presign?.success || !presign?.data?.uploadUrl) {
          throw new Error(presign?.message || "Could not prepare upload.");
        }
        await putCaptureBlob(
          presign.data.uploadUrl,
          take.blob,
          presign.data.contentType || take.mime || fileType,
          onProgress
        );
        const doneRes = await completePublicCapture(token, {
          mediaUrl: presign.data.mediaUrl,
          kind,
          type: take.mime,
          lat: take.lat,
          lng: take.lng,
          accuracyM: take.accuracyM,
          capturedAt: take.capturedAt,
          note: note || take.note,
          finalize,
        });
        if (!doneRes?.success) {
          throw new Error(doneRes?.message || "Saved on this phone, but mapping failed.");
        }
        return doneRes.data as PublicCaptureSession & { remainingUploads?: number; usedCount?: number };
      } catch (e: any) {
        lastError = e?.message || lastError;
        await sleep(1500 * Math.pow(2, attempt));
      }
    }
    throw new Error(lastError);
  };

  const onSend = async () => {
    const pending = cells
      .map((cell, slotIndex) => (cell.take && !cell.sent ? { ...cell.take, slotIndex } : null))
      .filter((item): item is PendingCapture => Boolean(item));
    if (!pending.length) return;
    setConfirmSend(false);
    setSending(true);
    setProgress(0);
    setError("");
    try {
      let next = [...cells];
      let lastResult: PublicCaptureSession | undefined;
      for (let i = 0; i < pending.length; i += 1) {
        const take = pending[i];
        const result = await sendOne(take, i === pending.length - 1, (n) => {
          const base = (i / pending.length) * 100;
          setProgress(Math.min(99, Math.round(base + n / pending.length)));
        });
        if (next[take.slotIndex]?.previewUrl) {
          URL.revokeObjectURL(next[take.slotIndex].previewUrl);
        }
        next[take.slotIndex] = { take: null, previewUrl: "", sent: true };
        setCells([...next]);
        lastResult = result;
      }
      setProgress(100);
      await persistCells(next);
      await clearPendingCapture(token).catch(() => undefined);
      if (!lastResult?.submitted && lastResult?.status !== "SUBMITTED") {
        const closed = await finalizePublicCapture(token).catch(() => null);
        if (closed?.success && closed.data) {
          lastResult = { ...lastResult, ...closed.data };
        }
      }
      lockSession(lastResult);
    } catch (e: any) {
      setError(
        `${e?.message || "Send failed."} Takes still on this phone stay here. Keep this page open and tap Send again.`
      );
    } finally {
      setSending(false);
    }
  };

  const retake = async (index: number) => {
    const cell = cells[index];
    if (cell?.previewUrl) URL.revokeObjectURL(cell.previewUrl);
    const next = cells.map((item, i) => (i === index ? { take: null, previewUrl: "", sent: false } : item));
    setCells(next);
    setReviewSlot(null);
    setCameraSlot(index);
    void startCamera(true);
    await persistCells(next);
  };

  const openCell = (index: number) => {
    if (!open || sending) return;
    const cell = cells[index];
    if (!cell || cell.sent) return;
    if (cell.take && cell.previewUrl) {
      setCameraSlot(null);
      setReviewSlot(index);
      return;
    }
    // Edge case: iOS denies location with no prompt if camera starts in the same tap.
    if (askingRef.current === "geo") return;
    if (!geoReadyRef.current) {
      requestLocation(true);
      return;
    }
    setReviewSlot(null);
    setCameraSlot(index);
    void startCamera(true);
  };

  const closeCamera = () => {
    if (recording) return;
    setCameraSlot(null);
    stopStream();
  };

  const setScale = (next: number) => {
    const clamped = Math.min(3, Math.max(1, next));
    pinchScaleRef.current = clamped;
    setReviewScale(clamped);
  };

  const canCapture = Boolean(cameraOpen && geoReady && cameraReady && !recording && !sending);
  const processTitle = String(session?.processTitle || "").trim();
  const showPermBanner = Boolean(open && !geoReady);
  const permissionPanel = (
    <PermissionRetryPanel
      allowsVideo={allowsVideo}
      cameraReady={cameraReady}
      micReady={micReady}
      geoReady={geoReady}
      camBlocked={camBlocked}
      micBlocked={micBlocked}
      geoBlocked={geoBlocked}
      asking={asking}
      permMessage={permMessage}
      onCamera={() => startCamera(true)}
      onMic={() => requestMicrophone()}
      onGeo={() => requestLocation()}
      onRetryAll={() => retryNextPermission()}
    />
  );
  const confirmCopy =
    emptyCount > 0
      ? `Send ${pendingCount === 1 ? "1 take" : `${pendingCount} takes`} now? ${emptyCount} empty square${
          emptyCount === 1 ? "" : "s"
        } will close and this link cannot be used again.`
      : `Send ${pendingCount === 1 ? "1 take" : `${pendingCount} takes`} now? This link will close after they are saved.`;

  return (
    <div className="capture-public">
      <header className="capture-public__top">
        <Link to="/home" className="capture-public__brand">
          HastKalaPramanak
        </Link>
        <span className="capture-public__eyebrow">Live field capture</span>
      </header>
      <main className="capture-public__main">
        {loading ? (
          <p className="capture-public__status">Opening this capture link…</p>
        ) : showSaved ? (
          <>
            <h1>Sent</h1>
            <p className="capture-public__meta">
              These live takes are mapped to {processTitle || "the process"} on{" "}
              {session?.productName || "the product"}. You can close this page.
            </p>
          </>
        ) : (
          <>
            <h1>{processTitle || "Capture"}</h1>
            <p className="capture-public__meta">
              {session?.productName || "Product"}
              {processTitle ? ` · ${processTitle}` : ""}
              {session?.instruction ? ` — ${session.instruction}` : ""}
            </p>
            <p className="capture-public__hint">
              Tap a square for a full-screen camera. After you click or record, you return here. Open a take to zoom,
              watch it full screen, or retake it. Send closes unused squares.
            </p>
            {geoLabel ? <p className="capture-public__geo">Geotag ready · {geoLabel}</p> : null}
            {showPermBanner ? permissionPanel : null}
            <div className="capture-public__grid" role="group" aria-label="Four capture squares">
              {cells.slice(0, maxUploads).map((cell, index) => (
                <button
                  key={index}
                  type="button"
                  className={`capture-public__cell${cell.sent ? " is-sent" : ""}${
                    cell.take ? " is-filled" : ""
                  }`}
                  aria-label={
                    cell.sent
                      ? `Saved square ${index + 1}`
                      : cell.take
                      ? `Review take ${index + 1}`
                      : `Capture square ${index + 1}`
                  }
                  onClick={() => openCell(index)}
                >
                  {cell.previewUrl && cell.take?.kind === "VIDEO" ? (
                    <>
                      <video src={cell.previewUrl} muted playsInline />
                      <span className="capture-public__play" aria-hidden="true" />
                    </>
                  ) : cell.previewUrl ? (
                    <img src={cell.previewUrl} alt={`Take ${index + 1}`} />
                  ) : cell.sent ? (
                    <span className="capture-public__cell-label">Saved</span>
                  ) : (
                    <span className="capture-public__cell-label">{index + 1}</span>
                  )}
                  {cell.take && !cell.sent ? (
                    <span className="capture-public__cell-kind">{cell.take.kind === "VIDEO" ? "Video" : "Photo"}</span>
                  ) : null}
                  {cell.take && cell.take.lat != null ? (
                    <span className="capture-public__cell-geo">
                      {formatCaptureGeo({
                        lat: cell.take.lat,
                        lng: cell.take.lng,
                        accuracyM: cell.take.accuracyM,
                        at: cell.take.capturedAt,
                      })}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
            <textarea
              maxLength={500}
              placeholder="Optional note for these takes"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            {pendingCount || sending ? (
              <>
                <p className="capture-public__hint">
                  Keep this page open until sent. Sending now closes empty squares on this link.
                </p>
                {sending ? (
                  <>
                    <div
                      className="capture-public__progress"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={progress}
                      aria-label="Upload progress"
                    >
                      <span style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
                    </div>
                    <p className="capture-public__progress-label">{progress}%</p>
                  </>
                ) : null}
                <div className="capture-public__actions">
                  <button
                    className="capture-public__btn"
                    type="button"
                    disabled={sending}
                    onClick={() => setConfirmSend(true)}
                  >
                    {sending
                      ? `Sending… ${progress}%`
                      : pendingCount === 1
                      ? "Send 1 take"
                      : `Send ${pendingCount} takes`}
                  </button>
                </div>
              </>
            ) : null}
            {error ? <p className="capture-public__error">{error}</p> : null}
          </>
        )}
      </main>

      {cameraOpen ? (
        <div className="capture-public__sheet" role="dialog" aria-label={`Full screen camera for square ${(cameraSlot ?? 0) + 1}`}>
          <div className="capture-public__sheet-stage">
            <video ref={videoRef} className="capture-public__sheet-media" playsInline muted autoPlay />
            {!cameraReady || !geoReady ? (
              <div className="capture-public__sheet-retry">
                <PermissionRetryPanel
                  allowsVideo={allowsVideo}
                  cameraReady={cameraReady}
                  micReady={micReady}
                  geoReady={geoReady}
                  camBlocked={camBlocked}
                  micBlocked={micBlocked}
                  geoBlocked={geoBlocked}
                  asking={asking}
                  permMessage={permMessage}
                  tone="sheet"
                  onCamera={() => startCamera(true)}
                  onMic={() => requestMicrophone()}
                  onGeo={() => requestLocation()}
                  onRetryAll={() => retryNextPermission()}
                />
              </div>
            ) : null}
          </div>
          {recording ? <div className="capture-public__timer">{secondsLeft}s</div> : null}
          <div className="capture-public__sheet-bar">
            <p className="capture-public__sheet-title">
              Square {(cameraSlot || 0) + 1} · {recording ? "Recording" : "Full screen camera"}
            </p>
            {geoLabel ? <p className="capture-public__geo">{geoLabel}</p> : null}
            <div className="capture-public__actions">
              {allowsPhoto ? (
                <button className="capture-public__btn" type="button" disabled={!canCapture} onClick={onClickPhoto}>
                  Click photo
                </button>
              ) : null}
              {allowsVideo ? (
                recording ? (
                  <button className="capture-public__btn capture-public__btn--danger" type="button" onClick={stopRecording}>
                    Stop
                  </button>
                ) : (
                  <button className="capture-public__btn" type="button" disabled={!canCapture} onClick={onRecord}>
                    Record video
                  </button>
                )
              ) : null}
              <button
                className="capture-public__btn capture-public__btn--ghost"
                type="button"
                disabled={recording}
                onClick={closeCamera}
              >
                Back
              </button>
            </div>
            {error ? <p className="capture-public__error">{error}</p> : null}
          </div>
        </div>
      ) : null}

      {reviewCell?.take && reviewCell.previewUrl && reviewSlot != null ? (
        <div className="capture-public__sheet capture-public__sheet--review" role="dialog" aria-label="Review this take">
          <div
            className={`capture-public__sheet-zoom ${frameClass(mediaFrame)}`}
            onTouchStart={(event) => {
              if (event.touches.length === 2) {
                pinchStartRef.current = touchDistance(event.touches);
                pinchScaleRef.current = reviewScale;
              }
            }}
            onTouchMove={(event) => {
              if (event.touches.length < 2 || !pinchStartRef.current) return;
              event.preventDefault();
              const next = pinchScaleRef.current * (touchDistance(event.touches) / pinchStartRef.current);
              setScale(next);
            }}
          >
            <div className="capture-public__sheet-zoom-stage" style={{ transform: `scale(${reviewScale})` }}>
              {reviewCell.take.kind === "VIDEO" ? (
                <video
                  src={reviewCell.previewUrl}
                  controls
                  playsInline
                  onLoadedMetadata={(event) =>
                    setMediaFrame(frameFromSize(event.currentTarget.videoWidth, event.currentTarget.videoHeight))
                  }
                />
              ) : (
                <img
                  src={reviewCell.previewUrl}
                  alt={`Take ${reviewSlot + 1}`}
                  onLoad={(event) =>
                    setMediaFrame(frameFromSize(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight))
                  }
                />
              )}
            </div>
          </div>
          <div className="capture-public__sheet-bar">
            <p className="capture-public__sheet-title">
              Square {reviewSlot + 1} · {reviewCell.take.kind === "VIDEO" ? "Video" : "Photo"}
            </p>
            <div className="capture-public__actions">
              <button
                className="capture-public__btn capture-public__btn--ghost"
                type="button"
                onClick={() => setScale(reviewScale + 0.25)}
              >
                Zoom in
              </button>
              <button
                className="capture-public__btn capture-public__btn--ghost"
                type="button"
                onClick={() => setScale(reviewScale - 0.25)}
              >
                Zoom out
              </button>
              <button
                className="capture-public__btn capture-public__btn--ghost"
                type="button"
                disabled={sending}
                onClick={() => retake(reviewSlot)}
              >
                Retake
              </button>
              <button className="capture-public__btn" type="button" onClick={() => setReviewSlot(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmSend ? (
        <div className="capture-public__lightbox" role="presentation">
          <div className="capture-public__lightbox-card capture-public__lightbox-card--confirm" role="dialog" aria-label="Confirm send">
            <p className="capture-public__meta">{confirmCopy}</p>
            <div className="capture-public__actions">
              <button className="capture-public__btn" type="button" onClick={onSend}>
                Send and close
              </button>
              <button
                className="capture-public__btn capture-public__btn--ghost"
                type="button"
                onClick={() => setConfirmSend(false)}
              >
                Keep editing
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CapturePublicPage;
